import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';

import Checkbox from '../../../components/Checkbox/Checkbox';
import { ErrorMessage } from '../../../components/ErrorMessage/ErrorMessage';
import CheckMark from '../../../components/SVGIcons/CheckMark';
import { analytics } from '../../../services/analytics';
import { useStores } from '../../../stores/useStores';

import classes from './SignUpConsents.module.scss';

const SignUpConsents = ({
  isAgeConfirmed,
  setIsAgeConfirmed,
  isTermsAccepted,
  setIsTermsAccepted,
  signUpMarketingConsents,
  marketingConsents,
  onChangeMarketingConsent,
  approveError,
}: {
  isAgeConfirmed: boolean;
  setIsAgeConfirmed: (value: boolean) => void;
  isTermsAccepted: boolean;
  setIsTermsAccepted: (value: boolean) => void;
  signUpMarketingConsents: Array<{ id: string; text: string }>;
  marketingConsents: Record<string, boolean>;
  onChangeMarketingConsent: (checked: boolean, id: string) => void;
  approveError?: string;
}) => {
  const { t } = useTranslation();
  const { liveGeoStore } = useStores();
  const { territoryLegalAge } = liveGeoStore.currentGeo ?? {};
  const legalAge = territoryLegalAge || 18;

  const onAgeChange = (checked: boolean) => {
    if (checked) {
      void analytics.sendEvent('age_approve', {
        event_category: 'sign_up',
        event_action: 'click',
        event_type: 'checkbox',
        event_value: 'age',
      });
    }
    setIsAgeConfirmed(checked);
  };

  const onTermsChange = (checked: boolean) => {
    if (checked) {
      void analytics.sendEvent('age_approve', {
        event_category: 'sign_up',
        event_action: 'click',
        event_type: 'checkbox',
        event_value: 'terms_of_service',
      });
    }
    setIsTermsAccepted(checked);
  };

  return (
    <div className={classes.signUpSections}>
      <div className={classNames(classes.approveRow, classes.ageRow)}>
        <Checkbox
          checked={isAgeConfirmed}
          className={classes.checkbox}
          dataTestId="age-confirmation-checkbox"
          icon={<CheckMark />}
          id="age-confirmation"
          onChange={onAgeChange}
        />
        <label
          className={classNames(classes.approveText, classes.ageLabel)}
          data-testid="age-confirmation-label"
          htmlFor="age-confirmation"
        >
          <span style={{ color: !isAgeConfirmed ? 'red' : undefined }}>*</span>{' '}
          {t('auth.ageConfirmation', { legalAge })}
        </label>
      </div>

      {signUpMarketingConsents.map((consent) => {
        const isChecked = marketingConsents[consent.id] ?? false;

        return (
          <div key={consent.id} className={classNames(classes.approveRow, classes.consentRow)}>
            <Checkbox
              checked={isChecked}
              dataTestId={`sign-up-marketing-consent-${consent.id}`}
              icon={<CheckMark />}
              id={consent.id}
              onChange={(checked) => onChangeMarketingConsent(checked, consent.id)}
            />
            <label className={classes.approveText} htmlFor={consent.id}>
              {consent.text}
            </label>
          </div>
        );
      })}

      <div className={classNames(classes.approveRow, classes.termsRow)}>
        <Checkbox checked={isTermsAccepted} icon={<CheckMark />} id="terms-confirmation" onChange={onTermsChange} />
        <label
          className={classNames(classes.approveText, classes.termsLabel)}
          data-testid="signup-consents-terms-label"
          htmlFor="terms-confirmation"
        >
          <Trans
            components={{
              star: <span style={{ color: !isTermsAccepted ? 'red' : undefined }} />,
              terms: <a className={classes.termsLink} href="/pages/terms-of-service" target="_blank" />,
              privacy: <a className={classes.termsLink} href="/pages/privacy-policy" target="_blank" />,
            }}
            i18nKey="auth.acceptTermsCheckboxLabel"
          />
        </label>
      </div>

      {approveError && (!isAgeConfirmed || !isTermsAccepted) && (
        <ErrorMessage className={classes.approveError}>{approveError}</ErrorMessage>
      )}
    </div>
  );
};

export default SignUpConsents;
